<template>
  <config-base class="opc-ze" :saas-mode="saasMode" show-plan-details>
    <template #config>
      <div class="opc-ze__mode" v-if="saasModeList.length > 1">
        <div class="opc-ze__mode__inner opc-ze__mode__row">
          <p class="opc-ze__mode__title">{{$t('billing.events.choose_payment_option')}}</p>
          <zm-button data-testid="checkout-button-60" class="opc-ze__mode__diff" type="link" @click="showDiffModal">{{$t('billing.events.compare_feature_title')}}</zm-button>
        </div>
        <zm-radio-group class="opc-ze__mode__events-group" v-model="saasMode">
          <zm-radio data-testid="checkout-radio-61" v-for="item in saasModeList" :key="item.label" :label="item.label" :disabled="item.disabled">
            <p>{{ item.name }}</p>
          </zm-radio>
        </zm-radio-group>
      </div>
      <!-- consumption mode -->
      <config-consumption-row
        v-if="saasMode === EventModeMap.PAY_PER_ATTENDEE"
        :ref="EventModeMap.PAY_PER_ATTENDEE"
        :plan-type="planType"
        :is='configConsumptionRowName'
      />
      <!-- subscription mode -->
      <saas-capacity-config-list
        v-if="saasMode === EventModeMap.SUBSCRIPTION"
        :ref="EventModeMap.SUBSCRIPTION"
        :configRowName="configRowName"
        :config-plan-type="planType"
        :title="title"
        :config-capacity-key="capacityKey"
        :trackingPlanKey='trackingPlanKey'
        showMonthlyPriceFromCycle
      />
      <webinar-explain-diff-dialog :visible="webinarDialogVisible" @close="closeWebinarDialog" />
    </template>
  </config-base>
</template>

<script type="text/jsx" lang="jsx">
import _, { map } from 'lodash';
import { mapActions, mapGetters, mapMutations } from 'vuex';
import ConfigList from '@/components/capacity/config-list';
import { BILLING_CYCLE } from '@/common/constants';
import plansMixin from '@/plans/mx/plan';
import ConfigBase from '@/plans/EVENT_SAAS/config/base';
import planCommonMixin from '@/plans/EVENT_SAAS/mixin';
import { EventModeMap } from '@/plans/EVENT_SAAS/helper/constant';
import ConfigConsumptionRow from '@/plans/EVENT_SAAS/component/config-consumption-row';
import { isFromZeConsumption } from '@/plans/EVENT_SAAS/helper/utils';
import { getOnlinePlanName } from '@/common/product';
import { plansof } from '@/utils/chain';
import TwoWayControl from '@/plans/EVENT_SAAS/mixins/two-way-control';
import ConfigConsumptionRowV2 from '@/plans/EVENT_SAAS/component/config-consumption-row-v2';
import WebinarExplainDiffDialog from '@/plans/EVENT_SAAS/component/dialog/webinar-explain-diff-dialog.vue';
import { pageConfig } from '@/common/service/page-config';
const SaasCapacityConfigList = {
  mixins: [ConfigList, planCommonMixin, TwoWayControl],
  props: {
    title: String,
  },
  computed: {
    ...mapGetters(['currentEditPlan', 'basicConfig', 'basePlanTotalLicenses', 'getSelectedPlans', 'getTypeGroupSelectedPlans', 'getSelectedBasePlanInfo', 'getCurrentBasePlanInfo', 'hasBoughtDependPlan', 'boughtPlans']),
    currentSelectedBasePlan() {
      return _.get(this.getSelectedBasePlanInfo?.plans, [0]);
    },
    currentBasePlan() {
      return this.getCurrentBasePlanInfo?.plans?.[0] || {};
    },
    boughtCurrentPlans() {
      return plansof(this.boughtPlans).type(this.configPlanType).get();
    },
    currentUpgradePlan() {
      return this.boughtCurrentPlans.find(p => p.plan_code === pageConfig?.editConfig?.oldPlanCode);
    },
    isUpgradeOPFreeTrial() {
      return this.currentUpgradePlan?.isFreeTrial;
    },
    saasPlans() {
      return this.planMapFlat.filter((p) => p.type === this.configPlanType && p.isSaas);
    },
    // Store bought plans with both capacity and billing_cycle for accurate filtering
    boughtSaasPlansKey() {
      return _.map(_.filter(this.boughtCurrentPlans, (p) => p.isSaas && !p.isOnlineFreeTrial && !p.isFreeTrial), (p) => `${p.capacity}_${p.billing_cycle}`);
    },
    boughtSaasPlansCapacity() {
      return _.map(_.filter(this.boughtCurrentPlans, (p) => p.isSaas && !p.isOnlineFreeTrial && !p.isFreeTrial), (p) => p.capacity);
    },
    // all plans under the current product type
    planList() {
      const boughtPaidPlans = _.filter(this.boughtCurrentPlans, (p) => !p.isOnlineFreeTrial);

      if (this.isUpgradeOPFreeTrial) {
        const beforeList = _.filter(this.saasPlans, p => {
          return !this.boughtSaasPlansCapacity.includes(p.capacity);
        });

        const afterList = _.filter(beforeList, (p) => {
          const currentCapacity = this.currentEditPlan?.[this.capacityKey];
          const isBulkLicenseAccount = this.basicConfig?.isBulkLicenseAccount;

          if (isBulkLicenseAccount) {
            return currentCapacity === p?.[this.capacityKey];
          }

          return true;
        });

        return !afterList?.length ? beforeList : afterList;
      }

      return this.planMapFlat.filter((p) => {
        const isBoughtCapacity = map(boughtPaidPlans, this.capacityKey).includes(p[this.capacityKey]);
        return p.type === this.configPlanType && p.isSaas && !isBoughtCapacity;
      });
    },
    // all selected plans under the current product type
    selectedPlanList() {
      return this.getTypeGroupSelectedPlans[this.configPlanType]?.filter((p) => p.isSaas);
    },
    addCapacityBtnVisible() {
      return false;
    },
    mx_capacityTips() {
      return '';
    },
    editTotalLimitDesc() {
      return this.$t(
        this.hasBoughtDependPlan ?
          'billing.addon_license_limit_tips' :
          'billing.addon_license_limit_tips_without_contact',
        [this.title, this.$t(this.currentBasePlan.parentPlanNameKey || this.currentBasePlan.typeNameKey)]);
    },
  },
  methods: {
    ...mapMutations(['updateSelectPlan', 'batchRemovePlan', 'setUpdateBasePlan']),
    ...mapActions(['switchSelectPlan']),
    addDefaultPlan() {
      const defaultCapacity = _.get(this.allCapacityOrderList, [0]);
      const { plan_code, cycleValue } = _.get(this.capacityMap, [defaultCapacity, 0], {});
      const cycleDisabled = cycleValue >= 12 && this.mx_annualPlanDisabled?.disabled
      plan_code && this.updateSelectPlan({ plan_code, cartInfo: { selected_number: cycleDisabled ? 0 : 1 } });
    },
    removePlans() {
      this.batchRemovePlan(this.selectedPlanList);
    },
    mx_getMaxHosts() {
      return this.basePlanTotalLicenses || 999;
    },
    mx_getMonthlyPlan(capacityPlanList = []) {
      const monthlyPlan = _.find(capacityPlanList, (p) => p.billing_cycle === BILLING_CYCLE.MONTHLY);

      const defaultDisabledBillingCycle = {
        disabled: true,
        disabledInnerDesc: this.$t('billing.events.onlyForAttendeesDesc', [100]),
      }

      return { plan: monthlyPlan, ...(monthlyPlan ? {} : { plan: { billing_cycle: BILLING_CYCLE.MONTHLY }, ...defaultDisabledBillingCycle })  };
    },
    afterSwitchCapacityHook(oldPlan, newPlan) {
      const selectedBasePlan = this.currentSelectedBasePlan;

      if (newPlan?.billing_cycle === BILLING_CYCLE.ANNUAL && selectedBasePlan?.billing_cycle === BILLING_CYCLE.MONTHLY) {
        const newPlan = _.find(this.planMapFlat, (p) => p.type === selectedBasePlan?.type && p.billing_cycle === BILLING_CYCLE.ANNUAL);

        this.switchSelectPlan([selectedBasePlan.plan_code, newPlan?.plan_code,]);
        this.setUpdateBasePlan({ update: true, addTime: Date.now() });

        const message = this.$t('billing.common.billing_cycle_auto_update_desc', [
          getOnlinePlanName(selectedBasePlan),
          this.title,
        ]);

        this.$message({
          type: 'info',
          duration: 5000,
          message,
          showClose: true
        });
      }
    },
    handleLicenseChange(license) {
      const maxLicense = this.mx_getMaxHosts();
      const shouldShowLicenseAlert = (license >= maxLicense && maxLicense !== 999)
      this.mx_licenseAlert = shouldShowLicenseAlert ? this.editTotalLimitDesc : "";
    },
  },
};

export default {
  mixins: [plansMixin, planCommonMixin],
  components: {
    WebinarExplainDiffDialog,
    ConfigConsumptionRow,
    ConfigConsumptionRowV2,
    ConfigBase: {
      mixins: [ConfigBase, planCommonMixin],
      computed: { ...mapGetters(['planMapFlat']) },
      mounted() {
        const saasPlan = _.filter(this.planMapFlat, (p) => p.type === this.planType && p.isSaas);

        if (saasPlan?.length === 0 || isFromZeConsumption()) {
          this.title =  `${this.$t('billing.planform.title.zoom_events')} ${this.$t('billing.events.ppa.title')}`;
        }
      }
    },
    SaasCapacityConfigList,
  },
  data() {
    return {
      isExpanded: true,
      hasSelectedPlan: false,
      saasMode: '',
      saasModeList: [],
      EventModeMap,
      webinarDialogVisible: false
    };
  },
  computed: {
    ...mapGetters(['planMapFlat', 'getTypeGroupSelectedPlans', 'boughtPlans']),
    planList() {
      return this.planMapFlat.filter((p) => p.type === this.planType);
    },
    saasPlans() {
      return _.filter(this.planList, (p) => p.isSaas);
    },
    consumptionPlans() {
      return _.filter(this.planList, (p) => p.isPPA);
    },
    isSassPlansAvailable() {
      const not = this.saasPlans?.length === 0 || isFromZeConsumption();
      return !not;
    },
    selectedPlanList() {
      return this.getTypeGroupSelectedPlans[this.planType];
    },
    selectedSaasPlanList() {
      return _.filter(this.selectedPlanList, (p) => p.isSaas);
    },
    selectedPPAPlanList() {
      // TODO: revert the next line comment after May 2024. do nothing right now, not default selected "Pay Per Attendee" before May 2024
      // return _.filter(this.selectedPlanList, (p) => p.isPPA);
      return [];
    },
    configRowName() {
      return 'CapacityConfigRow';
    },
    configConsumptionRowName() {
      return 'ConfigConsumptionRow';
    }
  },
  watch: {
    saasMode(nv, ov) {
      if (nv && ov) {
        this.$refs[ov]?.removePlans?.();
        this.$nextTick(()=> {
          this.$refs[nv]?.addDefaultPlan?.();
        });
      }
    }
  },
  methods: {
    ...mapMutations(['updateSelectPlan', 'batchRemovePlan']),
    ...mapActions(['switchSelectPlan']),
    showDiffModal() {
      this.webinarDialogVisible = true
    },
    closeWebinarDialog() {
      this.webinarDialogVisible = false
    },
    initSaasMode() {
      const isUpgradeOPFreeTrial = this.boughtPlans.find(p => p.plan_code === pageConfig?.editConfig?.oldPlanCode && p.isFreeTrial);
      if (!isUpgradeOPFreeTrial) {
        this.saasModeList = [
          {
            label: EventModeMap.PAY_PER_ATTENDEE,
            name: this.$t('billing.events.ppa.title'),
            // TODO: remove next line after Pay Per attendee supported in May Release 2024
            disabled: true
          },
        ];
      }

      if (this.isSassPlansAvailable) {
        this.saasModeList.push({
          label: EventModeMap.SUBSCRIPTION,
          name: this.$t('billing.events.saas.title'),
        });
      }

      if (this.selectedSaasPlanList.length) {
        this.saasMode = EventModeMap.SUBSCRIPTION;
      } else if (this.selectedPPAPlanList.length) {
        this.saasMode = EventModeMap.PAY_PER_ATTENDEE;
      }
    },
  },
  created() {
    this.initSaasMode();
  },
};
</script>

<style lang="scss">
@import '@zoom/billing-responsive/styles/responsive.scss';

.opc-ze {
  &__head {
    &__desc {
      color: #666487;
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      line-height: 150%;
    }
    &__action {
      &.zbo-button {
        padding: 0px;
        margin-left: 4px;
        font-size: 14px;
        font-style: normal;
        font-weight: 500;
        line-height: 120%;
      }
      .zbo-button__slot::first-letter {
        text-transform: capitalize;
      }
    }
  }
  &__mode {
    margin-bottom: 24px;
    &__inner {
      display: flex;
      align-items: center;
      gap: 24px;
      margin-bottom: 8px;
    }
    @include media(mobile) {
      .opc-ze__mode__inner.opc-ze__mode__row {
        flex-direction: column;
        gap: 0;
        align-items: flex-start;
      }
    }
    &__title {
      font-size: 18px;
      font-weight: 700;
      line-height: 27px;
      text-align: left;
    }
    &__diff {
      font-size: 14px;
      font-style: normal;
      font-weight: 600;
      line-height: 120%;
    }

    &__events-group {
      &.zbo-radio-group {
        .zbo-radio__inner {
          top: 0px;
        }

        .zbo-radio__label--inner {
          text-transform: capitalize;
        }
      }
    }
  }
}
</style>
